import { recordSupportError } from './support-context'
/**
 * Browser-side structured logger for OpSyncPro frontend (Better Stack / @logtail/browser).
 *
 * Behavior:
 *   - VITE_BETTERSTACK_SOURCE_TOKEN set → ships logs to Better Stack
 *   - Otherwise → console-only (local dev)
 *
 * Auto-detects environment from hostname (uat / production / development).
 * Attaches base context: service, env, url, userAgent.
 */

import { Logtail } from '@logtail/browser'
import { redactRawText, redactSensitive, boundRedactedBody, scrubUrlString } from './redact'

const SOURCE_TOKEN = import.meta.env.VITE_BETTERSTACK_SOURCE_TOKEN
const INGESTING_HOST = import.meta.env.VITE_BETTERSTACK_INGESTING_HOST

// Detect environment from URL
const hostname = window.location.hostname
const env = hostname.includes('uat') || hostname.includes('staging')
  ? 'uat'
  : hostname.includes('localhost') || hostname.includes('127.0.0.1')
  ? 'development'
  : 'production'

let logtail = null

/**
 * Base context attached to every shipped log. Read at call time (not module load)
 * so the current URL is captured per-log. `url` is run through `redactUrl` so a
 * live single-use auth token in the address bar — the `token_hash` on
 * `/auth/confirm` (OPS-803) — never lands in Better Stack; the caller `context`
 * is redacted separately by `sanitizeContext`, but this base `url` bypassed it.
 * @returns {Record<string, unknown>}
 */
export function baseLogContext() {
  return {
    service: 'frontend',
    env,
    url: scrubUrlString(window.location.href),
    userAgent: navigator.userAgent,
  }
}

if (SOURCE_TOKEN && INGESTING_HOST) {
  logtail = new Logtail(SOURCE_TOKEN, {
    endpoint: `https://${INGESTING_HOST}`,
  })

  // Enrich every log with base context
  logtail.use((log) => ({ ...log, ...baseLogContext() }))
}

/**
 * Sanitize a caller-supplied log context at the PUBLIC log boundary so nothing
 * reaches BetterStack (or the dev console) unredacted — regardless of whether the
 * caller remembered to wrap with `serializeError()`.
 *
 * Redaction used to live only in `serializeError()` and the QuickList HTTP
 * client, so a plain `logError(msg, { error: err.message })` — the copy-paste
 * pattern still scattered across the app — shipped its context to Logtail
 * untouched and could leak a token/PII (OPS-56, Codex review finding #2).
 * Enforcing redaction HERE, once, makes the logging API leak-safe by default so
 * no future call site has to remember the helper. `serializeError()` output is
 * already sanitized, so re-running `redactSensitive` is idempotent (defense in
 * depth); `redactSensitive` also returns non-object input unchanged, so a
 * string/primitive context is preserved.
 * @param {unknown} context
 * @returns {unknown}
 */
function sanitizeContext(context) {
  return redactSensitive(context)
}

/**
 * Log an info-level message to Better Stack (or console in dev).
 * @param {string} msg
 * @param {Record<string, unknown>} [context]
 */
export function logInfo(msg, context = {}) {
  const safe = sanitizeContext(context)
  if (logtail) {
    logtail.info(msg, safe)
  }
  if (env === 'development') {
    console.log(`[BS:info] ${msg}`, safe)
  }
}

/**
 * Log a warning to Better Stack.
 * @param {string} msg
 * @param {Record<string, unknown>} [context]
 */
export function logWarn(msg, context = {}) {
  const safe = sanitizeContext(context)
  if (logtail) {
    logtail.warn(msg, safe)
  }
  if (env === 'development') {
    console.warn(`[BS:warn] ${msg}`, safe)
  }
}

/**
 * Log an error to Better Stack.
 * @param {string} msg
 * @param {Record<string, unknown>} [context]
 */
export function logError(msg, context = {}) {
  recordSupportError(msg, context)
  const safe = sanitizeContext(context)
  if (logtail) {
    logtail.error(msg, safe)
  }
  if (env === 'development') {
    console.error(`[BS:error] ${msg}`, safe)
  }
}

/**
 * Normalize a thrown value into a structured, log-safe context object so error
 * logs never collapse to an empty `()`.
 *
 * Real Errors keep `error` (message), `name` and `stack`, plus any HTTP
 * `status` / `responseBody` fields a caller attached to a fetch failure.
 * Non-Error throws (strings, numbers, plain objects, null/undefined) are
 * serialized into a usable `error` field instead of being dropped — which is
 * what produced `QuickList: listing creation failed ()` (OPS-56).
 *
 * This is the *final*, reusable log-safe boundary: whatever it returns is shipped
 * straight to BetterStack by the caller. So redaction is applied HERE, to every
 * catch input regardless of origin — `error` (message), `name`, `stack`, the
 * serialized form of string/object/primitive throws, and any attached
 * `responseBody`. A network rejection (`TypeError('Failed to fetch …')`), a
 * thrown string carrying a token, or a future caller that never went through the
 * QuickList HTTP client's redaction would otherwise leak a `Bearer`/OAuth token,
 * JWT, or email via `payload.error` or `payload.stack`. The QuickList client
 * still redacts on its own path too (defense in depth).
 *
 * @param {unknown} err - the caught value
 * @param {Record<string, unknown>} [extra] - extra context (e.g. `{ asin }`),
 *   redacted at this boundary; it adds fields but never overrides the sanitized
 *   core error fields (`error`/`name`/`stack`/`status`/`responseBody`).
 * @returns {Record<string, unknown>}
 */
export function serializeError(err, extra = {}) {
  const base = {}
  if (err instanceof Error) {
    base.error = redactRawText(err.message || err.name || 'Error')
    if (err.name) base.name = redactRawText(err.name)
    if (err.stack) base.stack = redactRawText(err.stack)
    // Preserve custom fields commonly attached to fetch/HTTP errors. The
    // responseBody may be a pre-bounded object/string from the QuickList client
    // or a raw, unbounded value from any other caller — redact AND size-bound it
    // here either way so this shared boundary is leak-safe and never ships an
    // oversized payload, regardless of origin (idempotent for already-bounded
    // input). `boundRedactedBody` lives in `./redact` so the bound is enforced at
    // the reusable boundary, not duplicated in the QuickList client.
    if (err.status !== undefined) base.status = err.status
    if (err.responseBody !== undefined) base.responseBody = boundRedactedBody(err.responseBody)
  } else if (typeof err === 'string') {
    base.error = redactRawText(err)
  } else if (err === null || err === undefined) {
    base.error = String(err)
  } else if (typeof err === 'object') {
    // Non-Error object/array throw. Redact STRUCTURALLY (key-name + value-shape)
    // BEFORE stringifying. Stringifying first and running only the raw-text pass
    // misses a secret under a compound/camelCase sensitive key — e.g.
    // `{ sessionToken: 'plain-secret', apiSecret: 'plain-secret' }` — because
    // RAW_KEYVALUE_RE's `\b` word boundaries don't fire inside `sessionToken` and
    // a plain (non-token-shaped) value isn't caught either. redactSensitive walks
    // the structure, redacting by sensitive key name AND running every string
    // value through redactRawText, so an object throw is leak-safe exactly like
    // the responseBody / extra paths already are.
    base.error = redactRawText(stringifyUnknownObject(err))
  } else {
    // Primitive throw (number, boolean, bigint, symbol, function).
    base.error = redactRawText(stringifyUnknown(err))
  }
  // `extra` reaches this final log boundary too, so it must be sanitized — a
  // caller (now or in future) could pass context carrying a token/PII, and an
  // unredacted spread would leak it straight to BetterStack. redactSensitive
  // walks the object, stripping sensitive keys and running every string value
  // through redactRawText. Spread `base` LAST so the sanitized core error fields
  // can never be clobbered by caller context — `serializeError(err, { error:
  // 'Bearer …' })` can neither leak nor overwrite the real error.
  const safeExtra = redactSensitive(extra)
  return { ...safeExtra, ...base }
}

/**
 * Stringify a non-Error OBJECT/array throw after structurally redacting it, so a
 * secret under a sensitive key (even compound/camelCase, or with a plain
 * non-token-shaped value) is stripped before serialization. redactSensitive
 * preserves the object's keys, so when its JSON has useful content we log that;
 * when the redacted form has no serializable content (e.g. a null-prototype
 * object, or one whose only props are functions), we fall back to the original's
 * guarded coercion — `stringifyUnknown` — so hostile/empty throws still degrade
 * to a safe marker ('[object Object]' / '[unserializable error]') exactly as
 * before instead of changing behavior. The caller wraps the result in
 * `redactRawText` for value-shape defense in depth.
 * @param {object} err
 * @returns {string}
 */
function stringifyUnknownObject(err) {
  try {
    const serialized = JSON.stringify(redactSensitive(err))
    if (serialized && serialized !== '{}') return serialized
  } catch {
    // Should not happen — redactSensitive returns a fresh, depth-capped structure
    // — but never let logging throw.
  }
  return stringifyUnknown(err)
}

/**
 * Coerce an arbitrary non-Error throw into a string that is useful in logs and
 * can never itself throw. `String(err)` is unsafe for hostile values — a
 * null-prototype object, or one whose `toString`/`Symbol.toPrimitive` throws,
 * raises "Cannot convert object to primitive value", which would blow up the
 * very catch block trying to log the original failure. Both coercion attempts
 * are guarded; the last resort is a static marker.
 * @param {unknown} err
 * @returns {string}
 */
function stringifyUnknown(err) {
  try {
    const serialized = JSON.stringify(err)
    if (serialized && serialized !== '{}') return serialized
  } catch {
    // JSON.stringify can throw on a BigInt or a circular structure.
  }
  try {
    return String(err)
  } catch {
    return '[unserializable error]'
  }
}

/**
 * Flush pending logs (call before page unload).
 */
export function flushLogs() {
  if (logtail) {
    logtail.flush()
  }
}

export default { logInfo, logWarn, logError, serializeError, flushLogs }
